import React from 'react';
import { SpaceStatus, STATUS_LABELS } from '../types';

/**
 * Props for SpaceStatusIndicator component
 */
export interface SpaceStatusIndicatorProps {
    /** The status to display */
    status: SpaceStatus;
    /** Optional size variant */
    size?: 'small' | 'medium';
    /** Whether to show the status label text */
    showLabel?: boolean;
}

/**
 * Color configuration for each status
 * Using semantic colors that match common conventions:
 * - Gray for inactive/draft states
 * - Blue for in-progress/building states
 * - Green for success/running states
 * - Orange/yellow for warning/stopped states
 * - Red for error states
 */
const STATUS_CONFIG: Record<SpaceStatus, { color: string; backgroundColor: string; pulseAnimation?: boolean }> = {
    draft: {
        color: '#6b7280',
        backgroundColor: '#f3f4f6',
    },
    building: {
        color: '#2563eb',
        backgroundColor: '#dbeafe',
        pulseAnimation: true,
    },
    running: {
        color: '#059669',
        backgroundColor: '#d1fae5',
    },
    stopped: {
        color: '#d97706',
        backgroundColor: '#fef3c7',
    },
    error: {
        color: '#dc2626',
        backgroundColor: '#fee2e2',
    },
};

/**
 * Status indicator dot component
 */
const StatusDot: React.FC<{ status: SpaceStatus; size: 'small' | 'medium' }> = ({ status, size }) => {
    const config = STATUS_CONFIG[status];
    const dotSize = size === 'small' ? 8 : 10;

    const dotStyle: React.CSSProperties = {
        width: dotSize,
        height: dotSize,
        borderRadius: '50%',
        backgroundColor: config.color,
        display: 'inline-block',
        flexShrink: 0,
    };

    if (config.pulseAnimation) {
        return (
            <span
                style={dotStyle}
                className="space-status-dot space-status-dot--pulse"
                data-testid="status-dot"
            />
        );
    }

    return <span style={dotStyle} className="space-status-dot" data-testid="status-dot" />;
};

/**
 * SpaceStatusIndicator - displays a visual status badge for a space
 *
 * Renders a colored dot with optional label text indicating the current
 * status of a space (draft, building, running, stopped, error).
 *
 * The building status includes a pulse animation to indicate active processing.
 *
 * @example
 * // Basic usage
 * <SpaceStatusIndicator status="running" />
 *
 * @example
 * // With label
 * <SpaceStatusIndicator status="building" showLabel />
 *
 * @example
 * // Small size without label (for compact lists)
 * <SpaceStatusIndicator status="draft" size="small" />
 */
const SpaceStatusIndicator: React.FC<SpaceStatusIndicatorProps> = ({
    status,
    size = 'medium',
    showLabel = true,
}) => {
    const config = STATUS_CONFIG[status];
    const label = STATUS_LABELS[status];

    const containerStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: size === 'small' ? 4 : 6,
        padding: size === 'small' ? '2px 6px' : '4px 8px',
        borderRadius: 4,
        backgroundColor: config.backgroundColor,
        fontSize: size === 'small' ? 11 : 12,
        fontWeight: 500,
        color: config.color,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace',
        lineHeight: 1,
    };

    return (
        <span
            style={containerStyle}
            className={`space-status-indicator space-status-indicator--${status} space-status-indicator--${size}`}
            data-testid="space-status-indicator"
            data-status={status}
            title={`Status: ${label}`}
        >
            <StatusDot status={status} size={size} />
            {showLabel && <span className="space-status-label">{label}</span>}
        </span>
    );
};

export default SpaceStatusIndicator;
